import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { rawFetch } from '../../services/api';

interface QueueItem {
  type: 'platform' | 'artist';
  artistName?: string;
  artistSlug?: string;
  membershipMemberId?: number;
}

interface ComplianceWizardProps {
  userReference: string;
  onComplete: () => void;
}

type SlideDirection = 'right' | 'left';
type AnimationPhase = 'enter' | 'exit' | 'idle';

export default function ComplianceWizard({ userReference, onComplete }: ComplianceWizardProps) {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('right');
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>('enter');
  const [displayIndex, setDisplayIndex] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    try {
      const res = await rawFetch(`/api/public/fanzone/${userReference}/compliance-queue`, {
        credentials: 'include',
      });
      if (!res.ok) {
        onComplete();
        return;
      }
      const json = await res.json() as { data: { queue: QueueItem[]; totalPending: number } };
      if (json.data.queue.length === 0) {
        onComplete();
        return;
      }
      setQueue(json.data.queue);
      setLoading(false);
    } catch {
      onComplete();
    }
  };

  const advanceSlide = useCallback((nextIndex: number) => {
    setSlideDirection('right');
    setAnimationPhase('exit');

    // Wait for exit animation to finish, then switch content and enter
    setTimeout(() => {
      setDisplayIndex(nextIndex);
      setCurrentIndex(nextIndex);
      setAnimationPhase('enter');
    }, 320);
  }, []);

  const submitAnswer = async (accepted: boolean) => {
    if (submitting) return;
    setSubmitting(true);
    setError('');

    const item = queue[currentIndex];
    const body: { type: string; accepted: boolean; artistSlug?: string } = {
      type: item.type,
      accepted,
    };
    if (item.type === 'artist') {
      body.artistSlug = item.artistSlug;
    }

    try {
      const res = await rawFetch(`/api/public/fanzone/${userReference}/compliance-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Failed to save' }));
        setError((errData as any).error || 'Failed to save your choice');
        setSubmitting(false);
        return;
      }

      // Advance to next question or complete
      if (currentIndex + 1 < queue.length) {
        advanceSlide(currentIndex + 1);
      } else {
        // Final card exit animation before completing
        setSlideDirection('right');
        setAnimationPhase('exit');
        setTimeout(() => onComplete(), 400);
      }
    } catch {
      setError('Network error — please try again');
    }
    setSubmitting(false);
  };

  // Determine animation class
  const getAnimationClass = (): string => {
    if (animationPhase === 'enter') {
      return slideDirection === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left';
    }
    if (animationPhase === 'exit') {
      return slideDirection === 'right' ? 'animate-slide-out-left' : 'animate-slide-out-right';
    }
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 animate-fade-in">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-electric-cyan/20 blur-xl animate-pulse" />
            <Loader2 className="w-10 h-10 text-electric-cyan animate-spin relative" />
          </div>
          <span className="text-xs text-text-muted font-mono tracking-wider uppercase">
            Preparing your preferences…
          </span>
        </div>
      </div>
    );
  }

  const current = queue[displayIndex];
  const progress = `${currentIndex + 1} / ${queue.length}`;
  const progressPercent = ((currentIndex + 1) / queue.length) * 100;

  return (
    <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4 overflow-hidden">
      {/* Ambient background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-electric-cyan/[0.03] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-electric-cyan/[0.02] rounded-full blur-[100px]" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <img src="/fans-audio-logo.png" alt="fans.audio" className="h-7 opacity-70" />
          <span className="text-xs text-text-muted font-mono tracking-wider">{progress}</span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1.5 bg-surface/80 rounded-full mb-8 overflow-hidden backdrop-blur-sm animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div
            className="h-full rounded-full transition-all duration-700 ease-out relative"
            style={{ width: `${progressPercent}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-electric-cyan to-cyan-dim rounded-full" />
            <div className="absolute inset-0 bg-electric-cyan/40 rounded-full blur-sm" />
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mb-8 animate-fade-in-up" style={{ animationDelay: '0.25s' }}>
          {queue.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                idx === currentIndex
                  ? 'w-6 bg-electric-cyan shadow-[0_0_8px_rgba(0,245,255,0.5)]'
                  : idx < currentIndex
                    ? 'w-1.5 bg-electric-cyan/50'
                    : 'w-1.5 bg-surface-light'
              }`}
            />
          ))}
        </div>

        {/* Animated card wrapper */}
        <div className="relative">
          <div
            ref={cardRef}
            key={displayIndex}
            className={`${getAnimationClass()}`}
          >
            {/* Card */}
            <div className="relative p-8 rounded-2xl border border-white/[0.06] bg-surface/60 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
              {/* Subtle top-edge glow */}
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-electric-cyan/30 to-transparent" />

              {current.type === 'platform' ? (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-electric-cyan/20 to-electric-cyan/5 flex items-center justify-center mb-6 border border-electric-cyan/10">
                    <span className="text-2xl">📡</span>
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2 tracking-tight">
                    Music Recommendations
                  </h2>
                  <p className="text-sm text-text-secondary leading-relaxed mb-8">
                    Would you like to receive music recommendations and discover similar artists from fans.audio?
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => submitAnswer(true)}
                      disabled={submitting}
                      className="group w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-electric-cyan to-cyan-dim text-deep-charcoal font-bold text-sm transition-all duration-300 hover:shadow-[0_4px_20px_rgba(0,245,255,0.4)] hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Absolutely
                          <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => submitAnswer(false)}
                      disabled={submitting}
                      className="w-full py-3.5 px-6 rounded-xl border border-white/[0.08] text-text-secondary font-medium text-sm hover:border-white/20 hover:text-white hover:bg-white/[0.03] transition-all duration-300 disabled:opacity-50"
                    >
                      No Thanks
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-electric-cyan/20 to-electric-cyan/5 flex items-center justify-center mb-6 border border-electric-cyan/10">
                    <span className="text-2xl">🎵</span>
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2 tracking-tight">
                    Stay Connected
                  </h2>
                  <p className="text-sm text-text-secondary leading-relaxed mb-8">
                    Would you like to keep up-to-date with{' '}
                    <span className="text-white font-semibold">{current.artistName}</span>'s
                    news, releases, and transmissions?
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => submitAnswer(true)}
                      disabled={submitting}
                      className="group w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-electric-cyan to-cyan-dim text-deep-charcoal font-bold text-sm transition-all duration-300 hover:shadow-[0_4px_20px_rgba(0,245,255,0.4)] hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Yes, I'm In
                          <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => submitAnswer(false)}
                      disabled={submitting}
                      className="w-full py-3.5 px-6 rounded-xl border border-white/[0.08] text-text-secondary font-medium text-sm hover:border-white/20 hover:text-white hover:bg-white/[0.03] transition-all duration-300 disabled:opacity-50"
                    >
                      No Thanks
                    </button>
                  </div>
                </>
              )}

              {error && (
                <p className="mt-4 text-xs text-red-400 text-center animate-fade-in">{error}</p>
              )}
            </div>
          </div>
        </div>

        <p className="text-[10px] text-text-muted text-center mt-8 font-mono tracking-wide animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          You can change these preferences any time from your My Collection settings.
        </p>
      </div>
    </div>
  );
}
